(() => {
  if (customElements.get('sega-runner-scene')) return;
  const clamp = (value) => Math.max(0, Math.min(1, value));
  const smooth = (value) => { const x = clamp(value); return x * x * (3 - 2 * x); };
  class RunnerScene extends HTMLElement {
    connectedCallback() {
      this.athlete = this.querySelector('.segastore-runner-scene__athlete');
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas.getContext('2d');
      this.words = [...this.querySelectorAll('[data-runner-word]')];
      this.elapsed = 0;
      this.particles = [];
      this.emission = 0;
      this.inViewport = !('IntersectionObserver' in window);
      this.reduced = matchMedia('(prefers-reduced-motion: reduce), (max-width: 749px)');
      this.reduceMotion = this.reduced.matches;
      this.clock = window.segastoreMotionClock;
      this.onChange = (event) => {
        this.reduceMotion = event?.type === 'change' ? event.matches : this.reduced.matches;
        this.loadImage();
        this.sync();
      };
      this.onResize = () => { this.resize(); this.onChange(); };
      this.frame = (now) => this.tick(now);
      this.reduced.addEventListener('change', this.onChange);
      document.addEventListener('visibilitychange', this.onChange);
      this.buttons = [...document.querySelectorAll('[data-sport-mark-toggle]')];
      this.controlObserver = new MutationObserver(this.onChange);
      this.buttons.forEach((button) => this.controlObserver.observe(button, { attributes:true, attributeFilter:['aria-pressed'] }));
      if ('IntersectionObserver' in window) {
        this.viewportObserver = new IntersectionObserver((entries) => {
          this.inViewport = entries.some((entry) => entry.isIntersecting);
          this.sync();
        }, { threshold:.08 });
        this.viewportObserver.observe(this);
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this);
      } else window.addEventListener('resize', this.onResize);
      this.makeDust();
      this.resize();
      this.loadImage();
      this.unsubscribeClock = this.clock?.subscribe({
        running: () => this.running(),
        advance: (now, framePhase) => this.renderFrame(framePhase % 8)
      });
      this.sync();
    }
    loadImage() {
      const source = this.reduceMotion && this.dataset.staticSheet ? this.dataset.staticSheet : this.dataset.sheet;
      if (source === this.imageSource) return;
      if (this.image) { this.image.onload = null; this.image.onerror = null; }
      this.imageSource = source;
      this.ready = false;
      this.dataset.ready = 'false';
      delete this.dataset.failed;
      const image = this.image = new Image();
      image.onload = () => {
        if (!this.isConnected || this.image !== image) return;
        this.ready = true;
        this.dataset.ready = 'true';
        this.sync();
      };
      image.onerror = () => {
        if (!this.isConnected || this.image !== image) return;
        this.dataset.failed = 'true';
        this.stop();
        this.renderStatic();
      };
      image.src = source;
    }
    disconnectedCallback() {
      this.unsubscribeClock?.();
      this.stop();
      this.viewportObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.controlObserver?.disconnect();
      this.reduced?.removeEventListener('change', this.onChange);
      document.removeEventListener('visibilitychange', this.onChange);
      window.removeEventListener('resize', this.onResize);
      if (this.image) { this.image.onload = null; this.image.onerror = null; }
      this.imageSource = null;
      this.ready = false;
    }
    makeDust() {
      this.texture = document.createElement('canvas');
      this.texture.width = this.texture.height = 64;
      const context = this.texture.getContext('2d');
      const gradient = context.createRadialGradient(32,32,0,32,32,32);
      gradient.addColorStop(0,'rgba(216,186,125,.64)');
      gradient.addColorStop(.25,'rgba(177,154,115,.38)');
      gradient.addColorStop(.6,'rgba(144,133,117,.13)');
      gradient.addColorStop(1,'rgba(144,133,117,0)');
      context.fillStyle = gradient;
      context.fillRect(0,0,64,64);
    }
    resize() {
      const rect = this.getBoundingClientRect();
      this.width = rect.width;
      this.height = rect.height;
      const ratio = Math.min(devicePixelRatio || 1, 1.5);
      this.canvas.width = Math.max(1,Math.round(this.width * ratio));
      this.canvas.height = Math.max(1,Math.round(this.height * ratio));
      this.context?.setTransform(ratio,0,0,ratio,0,0);
    }
    paused() { return this.buttons.some((button) => button.getAttribute('aria-pressed') === 'true'); }
    running() { return this.ready && this.isConnected && this.inViewport && !document.hidden && !this.reduceMotion && !this.paused() && this.width > 0 && this.context; }
    stop() { cancelAnimationFrame(this.raf); this.raf = null; this.last = null; }
    sync() {
      if (this.reduceMotion || !this.context) { this.stop(); this.renderStatic(); return; }
      if (this.paused()) { this.stop(); this.dataset.motion = 'paused'; this.revealWords(1,1,1); return; }
      if (!this.running()) { this.stop(); return; }
      if (!this.raf) { this.athlete.style.left = '50%'; this.athlete.style.top = '70%'; this.dataset.motion = 'running'; this.raf = requestAnimationFrame(this.frame); }
      this.clock?.schedule();
    }
    renderStatic() {
      this.dataset.motion = 'static';
      this.dataset.phase = 'message';
      this.context?.clearRect(0,0,this.width || 1,this.height || 1);
      this.athlete.style.left = '50%';
      this.athlete.style.top = '65%';
      this.athlete.style.transform = 'translate(-50%,-100%) scale(.55)';
      this.athlete.style.opacity = '.28';
      this.revealWords(1,1,1);
    }
    revealWords(first,second,fade) {
      this.words.forEach((word,index) => {
        const progress = index ? second : first;
        word.style.opacity = String(progress * fade);
        word.style.clipPath = `inset(0 ${(1-progress)*100}% 0 0)`;
        word.style.transform = `translateY(${(1-progress)*12}px)`;
      });
    }
    emit(x,y,scale) {
      if (this.particles.length >= 170) return;
      for (let i=0;i<5;i++) this.particles.push({
        x:x+(Math.random()-.5)*20*scale, y:y-4*scale,
        vx:-8-Math.random()*27, vy:-9-Math.random()*17,
        radius:(10+Math.random()*20)*Math.max(.28,scale),
        life:0, duration:2.8+Math.random()*2, alpha:.24+Math.random()*.32
      });
    }
    tick(now) {
      this.raf = null;
      if (!this.running()) { this.stop(); return; }
      const delta = this.last == null ? 0 : Math.max(0, now-this.last);
      const dt = Math.min(delta/1000,.08);
      this.last = now;
      this.elapsed += delta;
      const time = this.elapsed % 10500;
      const progress = clamp(time/2800);
      const compact = this.width<420;
      const scale = .09+(compact ? .61 : .77)*Math.pow(1-progress,1.05);
      const stride = time/600*Math.PI*2;
      const bob = Math.abs(Math.sin(stride))*7*scale;
      const x = this.width*(compact ? .18+.69*progress : .23+.63*progress)+Math.sin(stride)*2*scale;
      const y = this.height*(compact ? .55-.32*progress : .84-.6*progress)-bob;
      const phase = time<2800 ? 'running' : time<9000 ? 'message' : 'reset';
      if (!this.clock) this.renderFrame(Math.floor(time/75)%8);
      if (this.dataset.phase !== phase) this.dataset.phase = phase;
      this.athlete.style.transform = `translate(-50%,-100%) translate3d(${x-this.width*.5}px,${y-this.height*.7}px,0) scale(${scale})`;
      this.athlete.style.opacity = String(smooth(time/120)*(1-smooth((time-2250)/550)));
      this.revealWords(smooth((time-1500)/750),smooth((time-1800)/900),1-smooth((time-9000)/1200));
      if (time>60 && time<2500) {
        this.emission += dt;
        while (this.emission>.075) { this.emit(x,y,scale); this.emission-=.075; }
      } else this.emission = 0;
      this.context.clearRect(0,0,this.width,this.height);
      this.particles = this.particles.filter((particle) => particle.life<particle.duration);
      this.particles.forEach((particle) => {
        particle.life += dt;
        particle.x += particle.vx*dt;
        particle.y += particle.vy*dt;
        particle.vy += 3*dt;
        const age = particle.life/particle.duration;
        const radius = particle.radius*(1+age*2.6);
        this.context.globalAlpha = particle.alpha*Math.sin(Math.PI*clamp(age));
        this.context.drawImage(this.texture,particle.x-radius,particle.y-radius,radius*2,radius*2);
      });
      this.context.globalAlpha = 1;
      this.raf = requestAnimationFrame(this.frame);
    }
    renderFrame(frame) {
      if (this.dataset.frame === String(frame)) return;
      this.dataset.frame = String(frame);
      this.athlete.style.backgroundPosition = `${(frame%4)*100/3}% ${frame<4?0:100}%`;
    }
  }
  customElements.define('sega-runner-scene',RunnerScene);
})();
